import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiCreditCard, FiExternalLink, FiClock, FiCheckCircle, FiXCircle, FiRefreshCw, FiRotateCcw } = FiIcons;

export default function UserPaymentHistory({ userId }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) fetchPayments();
  }, [userId]);

  const fetchPayments = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('app_payments_v2')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (!error) setPayments(data);
    setLoading(false);
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'CONFIRMED': return { label: 'Pago', color: 'text-emerald-600 bg-emerald-50 border-emerald-100', icon: FiCheckCircle };
      case 'REFUNDED': return { label: 'Estornado', color: 'text-amber-600 bg-amber-50 border-amber-100', icon: FiRotateCcw };
      case 'FAILED': return { label: 'Falhou', color: 'text-red-600 bg-red-50 border-red-100', icon: FiXCircle };
      default: return { label: 'Pendente', color: 'text-blue-600 bg-blue-50 border-blue-100', icon: FiClock };
    }
  };

  if (loading) return (
    <div className="p-20 text-center">
      <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Carregando faturas...</p>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-2">
        <div>
          <h3 className="text-lg font-black text-slate-800 uppercase italic leading-none">Minhas Faturas</h3>
          <p className="text-[10px] text-slate-400 font-bold uppercase mt-1 tracking-widest">Histórico de pagamentos</p>
        </div>
        <button 
          onClick={fetchPayments} 
          className="p-3 bg-white border border-slate-100 rounded-2xl text-slate-400 hover:text-indigo-600 transition-all shadow-sm"
        >
          <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {payments.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-[2.5rem] p-16 text-center">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
              <SafeIcon icon={FiCreditCard} className="text-2xl" />
            </div>
            <h4 className="text-slate-900 font-black uppercase italic text-sm">Nenhuma fatura</h4>
            <p className="text-slate-400 text-[10px] font-bold uppercase mt-1 tracking-widest">Seus pedidos de crédito aparecerão aqui.</p>
          </div>
        ) : (
          payments.map((pay) => {
            const status = getStatusDisplay(pay.status);
            return (
              <motion.div 
                layout
                key={pay.id} 
                className="bg-white border border-slate-100 rounded-[2rem] p-5 flex items-center justify-between group hover:border-indigo-100 hover:shadow-xl hover:shadow-indigo-50/50 transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border ${status.color}`}>
                    <SafeIcon icon={status.icon} className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-black text-slate-900 text-lg tracking-tight">R$ {pay.amount}</p>
                      <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-tighter border ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                      {pay.credits} créditos • {new Date(pay.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {pay.payload?.invoiceUrl && (
                    <a 
                      href={pay.payload.invoiceUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-5 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-slate-200 hover:bg-slate-800 transition-all hover:scale-105"
                    >
                      Ver Fatura <SafeIcon icon={FiExternalLink} />
                    </a>
                  )}
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}